// src/pages/Privacy.js
import React from "react";

export default function Privacy() {
  const UPDATED = "November 3, 2025";

  return (
    <main className="privacy-page" role="main" style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Oiiko Legal Policy</h1>
        <p style={styles.subtitle}>
          Privacy Policy • Terms of Use • Data Processing Addendum (DPA)
        </p>
        <p style={styles.updated}>
          <em>Last updated: {UPDATED}</em>
        </p>
      </div>

      <div style={styles.contentWrap}>
        {/* Table of contents (left on desktop, top on mobile) */}
        <nav style={styles.toc} aria-label="Table of contents">
          <div style={styles.tocCard}>
            <div style={styles.tocHeading}>On this page</div>
            <ul style={styles.tocList}>
              <li>
                <a href="#about">1. About Oiiko</a>
              </li>
              <li>
                <a href="#collect">2. Information We Collect</a>
              </li>
              <li>
                <a href="#use">3. How We Use Your Information</a>
              </li>
              <li>
                <a href="#security">4. Data Storage & Security</a>
              </li>
              <li>
                <a href="#sharing">5. Data Sharing</a>
              </li>
              <li>
                <a href="#transfers">6. International Data Transfers</a>
              </li>
              <li>
                <a href="#retention">7. Data Retention</a>
              </li>
              <li>
                <a href="#rights">8. Your Rights (GDPR/CCPA)</a>
              </li>
              <li>
                <a href="#children">9. Children’s Privacy</a>
              </li>
              <li>
                <a href="#cookies">10. Cookies & Tracking</a>
              </li>
              <li>
                <a href="#billing">11. Subscriptions & Payments</a>
              </li>
              <li>
                <a href="#responsibilities">12. User Responsibilities</a>
              </li>
              <li>
                <a href="#termination">13. Termination</a>
              </li>
              <li>
                <a href="#ip">14. Intellectual Property</a>
              </li>
              <li>
                <a href="#liability">15. Limitation of Liability</a>
              </li>
              <li>
                <a href="#indemn">16. Indemnification</a>
              </li>
              <li>
                <a href="#law">17. Governing Law</a>
              </li>
              <li>
                <a href="#changes">18. Changes</a>
              </li>
              <li>
                <a href="#dpa">Data Processing Addendum (DPA)</a>
              </li>
              <li>
                <a href="#contact">Contact</a>
              </li>
            </ul>
          </div>
        </nav>

        {/* Document */}
        <article style={styles.doc}>
          <p>
            Welcome to Oiiko (“we,” “us,” or “our”). This page explains your
            rights and responsibilities when you use our website{" "}
            <strong>oiiko.com</strong> and our employee management platform (the
            “Service”). It combines our <strong>Privacy Policy</strong>,
            <strong> Terms of Use</strong>, and{" "}
            <strong>Data Processing Addendum (DPA)</strong> in one place. If you
            use Oiiko, you agree to these terms.
          </p>

          <h2 id="about" style={styles.h2}>
            1. About Oiiko
          </h2>
          <p>Oiiko LLC is a company registered in Texas, United States.</p>
          <p>
            Contact: <a href="mailto:support@oiiko.com">support@oiiko.com</a>.
          </p>
          <p>
            We provide an all-in-one employee management platform for businesses
            (“employers”) to manage their teams (“employees”).
          </p>

          <h2 id="collect" style={styles.h2}>
            2. Information We Collect
          </h2>
          <h3 style={styles.h3}>A. Employer Account Data</h3>
          <ul>
            <li>Name, contact number, company email, company role</li>
            <li>Password and password strength indicator</li>
            <li>
              Company name, industry, team size, address (street, city,
              state/province, postal code, country)
            </li>
            <li>Latitude/longitude (auto-captured) and company time zone</li>
            <li>
              Stripe customer ID, selected plan, plan price ID, billing cycle,
              subscription status, trial dates, employee limit
            </li>
            <li>How you heard about us</li>
          </ul>

          <h3 style={styles.h3}>B. Employee Data</h3>
          <ul>
            <li>
              Name, email, phone, gender, address, birth date, preferred first
              name
            </li>
            <li>
              Job title, department/group, role, employment type and status,
              start and end dates
            </li>
            <li>
              Require onboarding, self-onboarding flag, profile completion
              status
            </li>
            <li>
              Time bank enabled, PTO hours, vacation hours, sick leave hours
            </li>
            <li>
              Documents; geocoded address details (street, city, state/province,
              postal code, country, latitude, longitude)
            </li>
            <li>Company ID; employee document ID</li>
          </ul>

          <h3 style={styles.h3}>C. Time Tracking & Scheduling</h3>
          <ul>
            <li>User ID, employee name, company ID, date</li>
            <li>
              Clock-in/out times, hours worked, attendance (On Time / Late /
              Early)
            </li>
            <li>Shift start/end, week range, off day/holiday flags</li>
            <li>Clock-in location (IP address, optional coordinates)</li>
            <li>
              Company time zone, time-tracking settings (e.g., early/late
              minutes)
            </li>
            <li>
              Notifications (lateClockIn with severity), daily/weekly totals
            </li>
            <li>
              Employer edits (was edited, edit reason, forced clock-out and
              audit fields)
            </li>
          </ul>

          <h3 style={styles.h3}>
            D. Scheduling, Leave, Expense, Training, Help Desk, Analytics
          </h3>
          <ul>
            <li>
              <strong>Scheduling</strong>: week start/end, repeat weekly,
              activity types, paid flag, hours, notifications
            </li>
            <li>
              <strong>Leave</strong>: type, dates (full/half-day), reason,
              files/URLs, status & decisions, time zone, time bank deductions
            </li>
            <li>
              <strong>Expense</strong>: type, amount, merchant, method, date,
              description, receipt, status, reviewer notes, policy/budget
              settings
            </li>
            <li>
              <strong>Training</strong>: course meta (title, description, type,
              URL, deadline, thumbnail), assignments, progress, completion,
              notifications
            </li>
            <li>
              <strong>Help Desk</strong>: incident #, title, description,
              category, priority, impact, urgency, screenshots, SLA
              targets/breaches
            </li>
            <li>
              <strong>Analytics</strong>: aggregate metrics (counts, rates,
              trends, top contributors) across modules
            </li>
          </ul>

          <h2 id="use" style={styles.h2}>
            3. How We Use Your Information
          </h2>
          <ul>
            <li>Operate and improve the platform and features</li>
            <li>Provide dashboards, reports, and analytics</li>
            <li>
              Send transactional emails (onboarding, notifications, password
              resets)
            </li>
            <li>Process payments and manage subscriptions via Stripe</li>
            <li>
              Support, troubleshooting, abuse prevention, and enforcing terms
            </li>
          </ul>
          <p>
            <strong>We do not sell personal data.</strong>
          </p>

          <h2 id="security" style={styles.h2}>
            4. Data Storage & Security
          </h2>
          <p>
            We use Firebase, Firestore, Firebase Storage, and Stripe. Data is
            encrypted in transit and at rest within Google Cloud infrastructure.
            Access is restricted to authorized personnel for
            support/maintenance. If a data breach occurs, we will notify
            affected users and regulators as required by law.
          </p>

          <h2 id="sharing" style={styles.h2}>
            5. Data Sharing
          </h2>
          <p>
            We share limited data with trusted providers that help run Oiiko:
          </p>
          <ul>
            <li>Stripe for payments and subscriptions</li>
            <li>EmailJS for system emails</li>
          </ul>
          <p>
            Employers can access employee data they manage as part of the
            Service.
          </p>

          <h2 id="transfers" style={styles.h2}>
            6. International Data Transfers
          </h2>
          <p>
            Data may be processed in the United States or other countries where
            our partners operate. For GDPR, we rely on Standard Contractual
            Clauses or equivalent safeguards.
          </p>

          <h2 id="retention" style={styles.h2}>
            7. Data Retention
          </h2>
          <p>
            We keep data while your account is active or as needed for legal and
            operational reasons. Employers can delete their accounts at any
            time. Limited records may be retained for audit, fraud prevention,
            or compliance.
          </p>

          <h2 id="rights" style={styles.h2}>
            8. Your Rights (GDPR & CCPA)
          </h2>
          <p>
            You may request access, correction, deletion, portability, or
            restriction/ objection where applicable. Email{" "}
            <a href="mailto:support@oiiko.com">support@oiiko.com</a>.
          </p>

          <h2 id="children" style={styles.h2}>
            9. Children’s Privacy
          </h2>
          <p>Oiiko is not intended for individuals under 18.</p>

          <h2 id="cookies" style={styles.h2}>
            10. Cookies & Tracking
          </h2>
          <p>We do not use cookies or third-party tracking at this time.</p>

          <h2 id="billing" style={styles.h2}>
            11. Subscriptions & Payments
          </h2>
          <ul>
            <li>Payments are processed by Stripe</li>
            <li>Free trials may be offered</li>
            <li>No refunds after payment</li>
            <li>Cancel subscription or delete account any time</li>
            <li>Prices/plans may change; we’ll notify of material changes</li>
          </ul>

          <h2 id="responsibilities" style={styles.h2}>
            12. User Responsibilities
          </h2>
          <ul>
            <li>Use Oiiko for lawful business purposes</li>
            <li>No copying, reverse engineering, scraping, or misuse</li>
            <li>Keep account credentials confidential</li>
            <li>Employers manage their employee accounts</li>
          </ul>

          <h2 id="termination" style={styles.h2}>
            13. Termination
          </h2>
          <p>
            Employers may terminate at any time. We may suspend or terminate for
            violations or fraud.
          </p>

          <h2 id="ip" style={styles.h2}>
            14. Intellectual Property
          </h2>
          <p>All content, features, and software are owned by Oiiko LLC.</p>

          <h2 id="liability" style={styles.h2}>
            15. Limitation of Liability
          </h2>
          <p>
            The Service is provided “as is.” We are not liable for indirect,
            incidental, or consequential damages. Our total liability is limited
            to amounts paid in the previous 12 months.
          </p>

          <h2 id="indemn" style={styles.h2}>
            16. Indemnification
          </h2>
          <p>
            You agree to indemnify Oiiko for claims arising from your misuse or
            violations of these terms.
          </p>

          <h2 id="law" style={styles.h2}>
            17. Governing Law
          </h2>
          <p>Texas, United States.</p>

          <h2 id="changes" style={styles.h2}>
            18. Changes
          </h2>
          <p>
            We may update this document. For major changes we will notify via
            email or in-app.
          </p>

          <hr style={styles.hr} />

          <h2 id="dpa" style={styles.h2}>
            Data Processing Addendum (DPA)
          </h2>
          <p>
            This section applies when Oiiko processes personal data on behalf of
            employers (“Data Controllers”) as a “Data Processor.”
          </p>

          <h3 style={styles.h3}>A. Scope & Purpose</h3>
          <p>
            We process employee data only to provide the Service (time,
            scheduling, leave, expenses, training, help desk) under the
            employer’s instructions.
          </p>

          <h3 style={styles.h3}>B. Processor Obligations</h3>
          <ul>
            <li>Process data only on documented instructions</li>
            <li>Maintain appropriate technical and organizational security</li>
            <li>Ensure personnel confidentiality</li>
            <li>Notify the employer promptly of data breaches</li>
            <li>Assist with data subject requests</li>
            <li>Allow reasonable audits limited to verifying compliance</li>
          </ul>

          <h3 style={styles.h3}>C. Sub-Processors</h3>
          <ul>
            <li>Google Firebase / Firestore / Storage (Google Cloud)</li>
            <li>Stripe, Inc.</li>
            <li>EmailJS</li>
          </ul>
          <p>
            Each sub-processor must provide equivalent data protection
            standards.
          </p>

          <h3 style={styles.h3}>D. Deletion or Return</h3>
          <p>
            Upon termination or request we will delete or return personal data
            unless law requires retention.
          </p>

          <h3 style={styles.h3}>E. International Transfers</h3>
          <p>
            When transferring outside the EEA we use appropriate safeguards such
            as Standard Contractual Clauses.
          </p>

          <hr style={styles.hr} />

          <h2 id="contact" style={styles.h2}>
            Contact
          </h2>
          <p>
            Oiiko LLC • <a href="mailto:support@oiiko.com">support@oiiko.com</a>{" "}
            • Registered in Texas, USA
          </p>
        </article>
      </div>
    </main>
  );
}

/* ---------- minimal inline styles so it works without extra CSS ---------- */
const styles = {
  page: {
    padding: "32px 20px",
  },
  header: {
    maxWidth: 1040,
    margin: "0 auto 20px auto",
    padding: "0 12px",
  },
  title: {
    fontSize: 32,
    lineHeight: 1.2,
    marginBottom: 6,
    fontWeight: 800,
  },
  subtitle: {
    fontSize: 16,
    opacity: 0.8,
    marginBottom: 6,
  },
  updated: {
    fontSize: 14,
    opacity: 0.7,
  },
  contentWrap: {
    display: "grid",
    gridTemplateColumns: "280px 1fr",
    gap: 24,
    maxWidth: 1040,
    margin: "8px auto 40px auto",
    padding: "0 12px",
  },
  toc: {
    position: "relative",
  },
  tocCard: {
    position: "sticky",
    top: 16,
    padding: 16,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#fff",
  },
  tocHeading: {
    fontWeight: 700,
    fontSize: 14,
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  tocList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "grid",
    gap: 8,
    fontSize: 14,
  },
  doc: {
    padding: 20,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#fff",
    lineHeight: 1.6,
  },
  h2: { fontSize: 22, marginTop: 24 },
  h3: { fontSize: 18, marginTop: 16 },
  hr: { margin: "24px 0", border: 0, borderTop: "1px solid #e5e7eb" },
};

/* Responsive tweak for narrow screens */
if (typeof window !== "undefined") {
  const styleEl = document.createElement("style");
  styleEl.innerHTML = `
    @media (max-width: 900px) {
      .privacy-page ._contentWrap { grid-template-columns: 1fr !important; }
    }
  `;
}
